import * as Dialog from '@radix-ui/react-dialog';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useKeyboardHeight } from '../../../../hooks/use-keyboard-height';

type UrgentMessageModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (message: string) => void;
  isLoading?: boolean;
};

const Overlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
  background: rgba(0, 0, 0, 0.35);
  z-index: 1099;
`;

const CenteredContent = styled(Dialog.Content, {
  shouldForwardProp: (prop) => prop !== 'keyboardBottom',
})<{ keyboardBottom?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: ${(p) => p.keyboardBottom ?? 0}px;
  @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  transition: bottom 0.28s cubic-bezier(0.25, 0.1, 0.25, 1);
`;

const Container = styled.div`
  width: 328px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #26282d;
  border: 1px solid #2b2d33;
  border-radius: 16px;
  padding: 20px;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Heading = styled.h3`
  margin: 0;
  font-family: Inter;
  font-weight: 600;
  font-style: Semi Bold;
  font-size: 17px;
  line-height: 120%;
  color: #e9ecf5;
`;

const SubHeading = styled.p`
  margin: 0;
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 120%;
  color: #b7bdcb;
`;

const TextAreaWrap = styled.div`
  position: relative;
  width: 288px;
`;

const TextArea = styled.textarea<{ hasValue: boolean }>`
  width: 288px;
  height: 123px;
  resize: none;
  background: #23252a;
  border: 1px solid #2b2d33;
  border-radius: 12px;
  padding: 12px;
  outline: none;

  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: ${(p) => (p.hasValue ? '15px' : '12px')};
  line-height: 120%;
  color: #e9ecf5;

  &::placeholder {
    color: #8c92a3;
  }
`;

const Counter = styled.div`
  position: absolute;
  bottom: 8px;
  right: 12px;

  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 10px;
  line-height: 120%;
  text-align: right;
  color: #8c92a3;
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  width: 288px;
  height: 45px;
  padding: 12px 16px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  background: #5e84ed;

  font-family: Inter;
  font-weight: 600;
  font-style: Semi Bold;
  font-size: 15px;
  line-height: 140%;
  text-align: center;
  color: #ffffff;

  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.disabled ? 'none' : 'auto')};
`;

const SecondaryButton = styled.button`
  width: 288px;
  height: 45px;
  padding: 12px 16px;
  border-radius: 12px;
  background: transparent;
  border: 1px solid #5e84ed;
  cursor: pointer;

  font-family: Inter;
  font-weight: 600;
  font-style: Semi Bold;
  font-size: 15px;
  line-height: 140%;
  text-align: center;
  color: #5e84ed;
`;

const ButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const LoadingText = styled.p`
  margin: 0;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 120%;
  color: #b7bdcb;
  text-align: center;
`;

const UrgentMessageModal: React.FC<UrgentMessageModalProps> = ({ open, onOpenChange, onSend, isLoading = false }) => {
  const [value, setValue] = useState('');
  const max = 320;
  const count = value.length;
  const remaining = useMemo(() => `${count}/${max}`, [count]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { height: keyboardBottom, open: isKeyboardOpen } = useKeyboardHeight();

  const handleSend = () => {
    onSend(value.trim());
  };

  /** Tap on overlay / outside modal area */
  const handleOutsideTap = useCallback(
    (e: React.MouseEvent | React.PointerEvent) => {
      // Only act on direct taps (not bubbled from children)
      if (e.target !== e.currentTarget) return;

      if (isKeyboardOpen) {
        // Keyboard is open → just blur to close keyboard, keep modal open
        textareaRef.current?.blur();
      } else {
        // Keyboard is closed → close the modal
        onOpenChange(false);
      }
    },
    [isKeyboardOpen, onOpenChange],
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Overlay onClick={handleOutsideTap} />
        <CenteredContent
          keyboardBottom={keyboardBottom}
          onPointerDownOutside={(e) => {
            // Prevent Radix default close — we handle it ourselves
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onClick={handleOutsideTap}
          aria-describedby={undefined}
        >
          <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            Send urgent message
          </Dialog.Title>
          <motion.div
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
          >
            <Container>
              {isLoading ? (
                <LoadingContainer>
                  <LoadingSpinner />
                  <LoadingText>Sending message and opening conversation...</LoadingText>
                </LoadingContainer>
              ) : (
                <>
                  <HeaderBlock>
                    <Heading>Send urgent message</Heading>
                    <SubHeading>Chris will be notified immediately by text.</SubHeading>
                  </HeaderBlock>

                  <TextAreaWrap>
                    <TextArea
                      ref={textareaRef}
                      hasValue={value.length > 0}
                      maxLength={max}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder="Briefly describe what's urgent. Include timing and the best way to reach you."
                    />
                    <Counter>{remaining}</Counter>
                  </TextAreaWrap>

                  <ButtonsCol>
                    <PrimaryButton disabled={!value.trim()} onClick={handleSend}>
                      Send now
                    </PrimaryButton>
                    <SecondaryButton onClick={() => onOpenChange(false)}>Cancel</SecondaryButton>
                  </ButtonsCol>
                </>
              )}
            </Container>
          </motion.div>
        </CenteredContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UrgentMessageModal;
